import { __rest, __assign } from '../../../node_modules/tslib/tslib.es6.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import React__default from 'react';
import { Input } from 'reactstrap';
import Flatpickr from '../../../node_modules/react-flatpickr/build/index.js';
import classNames from 'classnames';
import SvgDate24 from '../../../icons/interface/Date24.js';
import SvgDate32 from '../../../icons/interface/Date32.js';
import { useDebounce } from '../../../hooks/useDebounce.js';
import './date-time-pickers-settings.js';

/** Hack to match classes between real and alt input */
var matchValidityClass = function (original, alt) {
    if (original.classList.contains('is-valid')) {
        alt.classList.add('is-valid');
        alt.classList.remove('is-invalid');
    }
    else if (original.classList.contains('is-invalid')) {
        alt.classList.add('is-invalid');
        alt.classList.remove('is-valid');
    }
    else {
        alt.classList.remove('is-valid', 'is-invalid');
    }
    if (original.classList.contains('form-control-lg')) {
        alt.classList.add('form-control-lg', 'ps-8');
        alt.classList.remove('form-control-sm', 'ps-7');
    }
    else if (original.classList.contains('form-control-sm')) {
        alt.classList.remove('form-control-lg', 'ps-8');
        alt.classList.add('form-control-sm', 'ps-7');
    }
    else {
        alt.classList.remove('form-control-sm', 'form-control-lg', 'ps-8');
        alt.classList.add('ps-7');
    }
};
var Datepicker = React__default.forwardRef(function (_a, ref) {
    var _b = _a.bsSize, bsSize = _b === void 0 ? 'md' : _b, className = _a.className, invalid = _a.invalid, options = _a.options, _c = _a.placeholder, placeholder = _c === void 0 ? 'Select date' : _c, valid = _a.valid, _d = _a.resetOptions, resetOptions = _d === void 0 ? false : _d, Icon = _a.Icon, props = __rest(_a, ["bsSize", "className", "invalid", "options", "placeholder", "valid", "resetOptions", "Icon"]);
    var wrapper = React__default.useRef(null);
    var flatpickrRef = React__default.useRef(null);
    var _e = React__default.useState(false), focused = _e[0], setFocused = _e[1];
    var debouncedFocused = useDebounce(focused, 50);
    React__default.useEffect(function () {
        var _a, _b, _c, _d, _e;
        var input = (_a = wrapper.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('input')[0];
        var altInput = (_b = wrapper.current) === null || _b === void 0 ? void 0 : _b.querySelectorAll('input')[1];
        var observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                if (mutation.type === 'attributes' &&
                    altInput &&
                    input &&
                    altInput.classList.contains('input')) {
                    if (mutation.attributeName === 'class') {
                        matchValidityClass(input, altInput);
                    }
                    else if (mutation.attributeName === 'aria-invalid') {
                        altInput.setAttribute('aria-invalid', input.getAttribute('aria-invalid') || 'false');
                    }
                }
            });
        });
        if (input) {
            observer.observe(input, {
                attributes: true,
                attributeFilter: ['class', 'aria-invalid']
            });
        }
        if ((_e = (_d = (_c = flatpickrRef === null || flatpickrRef === void 0 ? void 0 : flatpickrRef.current) === null || _c === void 0 ? void 0 : _c.flatpickr) === null || _d === void 0 ? void 0 : _d.calendarContainer) === null || _e === void 0 ? void 0 : _e.dataset) {
            // disable chromatic visual diff on calendar because current day changes every day, month changes every month
            flatpickrRef.current.flatpickr.calendarContainer.dataset.chromatic = 'ignore';
        }
        return function () {
            observer.disconnect();
        };
    }, []);
    React__default.useEffect(function () {
        var _a, _b;
        var iconSvg = (_a = wrapper.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('[data-selector="date-icon"]')[0];
        if (iconSvg) {
            if (options === null || options === void 0 ? void 0 : options.inline) {
                // fix Date icon position when calendar is inline
                var altInput = (_b = wrapper.current) === null || _b === void 0 ? void 0 : _b.querySelectorAll('input')[1];
                if (altInput) {
                    var altInputHeight = altInput.getBoundingClientRect().height;
                    iconSvg.style.setProperty('top', "".concat(altInputHeight / 2, "px"), 'important');
                }
            }
            else {
                iconSvg.style.removeProperty('top');
            }
        }
    }, [options === null || options === void 0 ? void 0 : options.inline]);
    React__default.useImperativeHandle(ref, function () {
        var _a;
        return ({
            flatpickr: (_a = flatpickrRef.current) === null || _a === void 0 ? void 0 : _a.flatpickr
        });
    });
    var DateIcon = Icon ? Icon : bsSize === 'lg' ? SvgDate32 : SvgDate24;
    var flatpickrOptions = React__default.useMemo(function () { return (__assign({ wrap: !(options === null || options === void 0 ? void 0 : options.inline) }, (resetOptions
        ? {}
        : {
            allowInput: !(options === null || options === void 0 ? void 0 : options.inline),
            altInput: true,
            altFormat: 'M j, Y',
            static: true
        }))); }, [options === null || options === void 0 ? void 0 : options.inline, resetOptions]);
    var inputProps = React__default.useMemo(function () { return ({
        className: bsSize === 'lg' ? 'ps-8' : 'ps-7',
        placeholder: placeholder,
        readOnly: true,
        valid: valid,
        invalid: invalid,
        bsSize: bsSize !== 'md' ? bsSize : undefined
    }); }, [bsSize, valid, invalid, placeholder]);
    /** Fix for when timepicker is shown inline causing keyboard problems */
    var focusShim = React__default.useCallback(function () {
        var _a, _b;
        if ((options === null || options === void 0 ? void 0 : options.inline) && (options === null || options === void 0 ? void 0 : options.enableTime) && (options === null || options === void 0 ? void 0 : options.noCalendar) && !debouncedFocused) {
            var flatpickrHour = (_b = (_a = wrapper.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('input.flatpickr-hour')) === null || _b === void 0 ? void 0 : _b[0];
            flatpickrHour === null || flatpickrHour === void 0 ? void 0 : flatpickrHour.focus();
        }
    }, [options === null || options === void 0 ? void 0 : options.inline, options === null || options === void 0 ? void 0 : options.enableTime, options === null || options === void 0 ? void 0 : options.noCalendar, debouncedFocused]);
    return (jsxs("div", __assign({ className: classNames('position-relative', className), ref: wrapper, "data-cy": "flatpickr-datepicker", onFocus: function () { return setFocused(true); }, onBlur: function () { return setFocused(false); } }, { children: [(options === null || options === void 0 ? void 0 : options.inline) ? (jsx(Flatpickr, __assign({}, props, { ref: flatpickrRef, options: __assign(__assign({}, flatpickrOptions), options), render: function (_a, ref) {
                    var defaultValue = _a.defaultValue;
                    return (jsx(Input, __assign({ innerRef: ref, defaultValue: defaultValue, "data-input": true, onFocus: focusShim }, inputProps)));
                } }))) : (jsx(Flatpickr, __assign({}, props, { ref: flatpickrRef, options: __assign(__assign({}, flatpickrOptions), options) }, { children: jsx(Input, __assign({ "data-input": true }, inputProps, { onFocus: focusShim })) }))), jsx(DateIcon, { className: "position-absolute top-50 start-0 translate-middle-y x5-ms-2 opacity-60 pe-none", "data-selector": "date-icon" })] })));
});
Datepicker.displayName = 'Datepicker';

export { Datepicker };
